<template>
    <div class="container mx-auto">
        <div class="flex justify-between" :class="[smallHeader ? 'my-3' : 'mb-4']">
            <div v-if="showHeader" class="flex">
                <h1 class="page-header" :class="{ 'text-2xl': smallHeader }">Estimated Waste</h1>
                <div class="hidden sm:flex ml-3 items-center text-gray-600">
                    <div :class="[smallHeader ? 'text-lg' : 'text-2xl']">/</div>
                    <div class="ml-2 text-xl" :class="[smallHeader ? 'pt-2px' : 'pt-1']">{{ sites.filtered.length }} of {{ sites.all.length }}</div>
                </div>
            </div>
            <div v-if="showFilters" class="flex items-center" :class="{'flex-1 justify-end pt-4 pb-3': ! showHeader}">
                <filters
                    data-type="sites"
                    sort-by="estimated_waste"
                    :sort-asc="false"
                    :filters="filters"
                    :selected-view-option="layout"
                    portal="no-sites-found"
                    @view-change="layout = $event"
                >
                    <template slot="filters">
                        <div>
                            <span class="mr-6">Show Archived Sites</span>
                            <toggle-button v-model="showArchivedSites" color="#38C172" sync></toggle-button>
                        </div>
                    </template>
                </filters>
                <slot name="action-buttons"></slot>
            </div>
        </div>

        <div class="flex flex-col lg:flex-row gap-x-6 gap-y-6">
            <div class="h-full flex-1">
                <estimated-waste-chart
                    title="Total Estimated Waste"
                    :estimated-waste="wastePercentage"
                ></estimated-waste-chart>
            </div>

            <div class="flex flex-1 flex-col gap-y-3">
                <div class="px-4 py-6 w-full bg-green-200 flex flex-col justify-center items-center rounded-t-lg shadow-lg gap-y-2">
                    <p data-precision="0" :data-originalvalue="totalNPV" class="is-currency text-4xl md:text-5xl text-green-600 font-bold">{{ formatCurrency(totalNPV) }}</p>
                    <p class="text-lg md:text-xl text-green-700 font-black">Total Portfolio NPV</p>
                </div>

                <div class="px-4 py-6 w-full bg-red-200 flex flex-col justify-center items-center rounded-b-lg shadow-lg gap-y-2">
                    <p data-precision="0" :data-originalvalue="Math.round(totalWaste * 100)" class="is-currency text-4xl md:text-5xl text-red-600 font-bold">
                        {{ formatCurrency(Math.round(totalWaste * 100)) }}
                    </p>
                    
                    <p class="text-lg md:text-xl text-red-700 font-black flex items-center gap-2">
                        Total Estimated Waste
                        <InfoIcon identifier="waste-info-totalestimatedwaste" />
                    </p>
                </div>

            </div>
        </div>

        <div class="mt-16 border-b border-gray-200 pb-2">
            <h1 class="module-header flex items-center gap-2">
                Top 10 Sites with Waste
                <InfoIcon identifier="waste-top10siteswithwaste" />
            </h1>
        </div>


        <div v-if="layout == 'grid'" class="mt-6 container">
            <div class="mt-5 grid grid-cols-6 gap-x-6 gap-y-12" :class="{ 'mx-auto': centerCards }">
                <div class="card col-span-6 md:col-span-3 2xl:col-span-2" v-for="(site, index) in limitBy(sites.filtered, 10)" :key="site.id" :class="{'hidden': ! showArchivedSites && site.archived_at !== null }">
                    <a
                        :key="site.id"
                        class="h-full relative flex items-stretch text-gray-800 cursor-pointer transform hover:no-underline hover:scale-105"
                        :href="site.view_url"
                    >
                        <div class="-mt-3 -ml-3 w-10 h-10 absolute inset-0 flex items-center justify-center bg-red-400 text-white font-black rounded-full shadow-md z-50">{{ index + 1 }}</div>
                        <div class="w-full flex border rounded-lg shadow-lg">
                            <div class="w-full flex flex-col">
                                <div class="flex flex-col">
                                    <div>
                                        <div class="absolute top-0 right-0 badge border rounded rounded-br-none" :class="site.status_color">
                                            <div class="flex items-baseline font-variant-all-small-caps space-x-1">
                                                <div v-if="site.lease_or_own == 'Owned'">Owned</div>
                                                <div v-else-if="site.leases.length && site.due_in_days > 0">Due in <span class="font-bold">{{ site.due_in_text }}</span></div>
                                                <div v-else-if="site.leases.length && site.due_in_days == 0">Expired</div>
                                                <div v-else>No Lease Information</div>
                                            </div>
                                        </div>
                                        <div
                                            class="flex w-full rounded-tl-full"
                                            :class="{ 'bg-red-400 text-white': site.is_upcoming }"
                                        >
                                            <div
                                                class="w-40 relative"
                                                :class="site.is_upcoming ? 'text-red-400' : 'text-white'"
                                            >
                                                <div v-if="site.archived_at !== null" class="mt-4 ml-4 absolute top-0 left-0 z-40" title="Site is Archived">
                                                    <inline-svg name="archived"></inline-svg>
                                                </div>
                                                <img class="w-full h-full max-h-[136px] object-cover border-r border-white rounded-tl-lg" :src="site.building.image.view_url">
                                                <inline-svg name="image-skewer-right"></inline-svg>
                                            </div>
                                            <div class="mx-4 mt-12 mb-10 flex flex-1 flex-col justify-center">
                                                <div class="uppercase">
                                                    <p class="text-lg font-semibold" :title="site.building.address_line_1">{{ site.building.address_line_1 | truncate(25) }}</p>
                                                    <p class="text-sm">{{ site.building.city }}, {{ site.building.state }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="p-6 bg-gray-50">
                                        <div class="grid grid-cols-2 gap-x-4 gap-y-4">
                                            <div class="px-5 py-3 flex flex-col bg-white rounded border border-gray-200 text-center">
                                                <span>{{ site.lease_start_date_formatted }}</span>
                                                <span class="font-variant-all-small-caps font-semibold">Lease Start</span>
                                            </div>

                                            <div class="px-5 py-3 flex flex-col bg-white rounded border border-gray-200 text-center">
                                                <span>{{ site.lease_end_date_formatted }}</span>
                                                <span class="font-variant-all-small-caps font-semibold">Lease End</span>
                                            </div>

                                            <div class="px-5 py-3 flex flex-col bg-white rounded border border-gray-200 text-center">
                                                <span>{{ site.utilization }}%</span>
                                                <span class="font-variant-all-small-caps font-semibold">Utilization</span>
                                            </div>

                                            <div class="px-5 py-3 flex flex-col bg-white rounded border border-gray-200 text-center">
                                                <span>{{ site.npv_discount_rate }}%</span>
                                                <span class="font-variant-all-small-caps font-semibold">NPV Discount</span>
                                            </div>

                                            <div class="px-5 py-3 flex flex-col bg-white rounded border border-gray-200 text-center">
                                                <span :data-originalvalue="site.shortened_lease_cost" class="is-shorthand-currency text-green-600 font-extrabold">${{ site.shortened_lease_cost }}</span>
                                                <span class="font-variant-all-small-caps font-semibold">Lease Cost</span>
                                            </div>

                                            <div class="px-5 py-3 flex flex-col bg-white rounded border border-gray-200 text-center">
                                                <span :data-originalvalue="site.shortened_remaining_lease_cost" class="is-shorthand-currency text-red-600 font-extrabold">${{ site.shortened_remaining_lease_cost }}</span>
                                                <span class="font-variant-all-small-caps font-semibold">Lease Remaining</span>
                                            </div>

                                            <div class="px-5 py-3 flex flex-col bg-white rounded border border-gray-200 text-center">
                                                <span :data-originalvalue="site.shortened_npv" class="is-shorthand-currency text-green-600 font-extrabold">${{ site.shortened_npv }}</span>
                                                <span class="font-variant-all-small-caps font-semibold">NPV</span>
                                            </div>

                                            <div class="px-5 py-3 flex flex-col bg-white rounded border border-gray-200 text-center">
                                                <span :data-originalvalue="site.shortened_estimated_waste" class="is-shorthand-currency text-red-600 font-extrabold">${{ site.shortened_estimated_waste }}</span>
                                                <span class="font-variant-all-small-caps font-semibold">Est Waste $</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </a>
                </div>
            </div>
        </div>

        <div v-if="layout == 'table' && sites.filtered.length" class="container">
            <table class="w-full">
                <thead class="cursor-pointer">
                    <tr>
                        <th class="pl-4"></th>
                        <th></th>
                        <th>Address</th>
                        <th class="text-center">Due In</th>
                        <th class="text-right">Lease Cost</th>
                        <th class="text-right">Remaining</th>
                        <th class="text-right">Utilization</th>
                        <th class="text-right">NPV Discount</th>
                        <th class="text-right">NPV</th>
                        <th class="text-right">Est Waste</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(site, index) in limitBy(sites.filtered, 10)" :key="site.id"
                        :class="{
                            'hidden': ! showArchivedSites && site.archived_at !== null,
                        }"
                    >
                        <td>
                            <div class="mx-auto w-10 h-10 flex items-center justify-center bg-red-400 text-white font-black rounded-full shadow-md z-40">{{ index + 1 }}</div>
                        </td>
                        <td class="relative h-16">
                            <div v-if="site.archived_at !== null" class="absolute mt-1 ml-1 z-40" title="Site is Archived">
                                <inline-svg name="archived" classes="w-2 h-2 text-white fill-current"></inline-svg>
                            </div>
                            <a :href="`#${site.id}`">
                                <img :src="site.building.image.view_url" class="mx-auto w-14 h-14 rounded-md shadow-md">
                            </a>
                            <a href="#_" class="lightbox" :id="`${site.id}`">
                                <img :src="site.building.image.view_url">
                            </a>
                        </td>
                        <td class="leading-normal">
                            <a :href="site.view_url">
                                <p class="text-sm font-semibold">{{ site.building.address_line_1 }}</p>
                                <p class="text-xs">{{ site.building.city }}, {{ site.building.state }}</p>
                            </a>
                        </td>
                        <td class="text-center">
                            <div class="badge" :class="site.status_color">
                                <div class="flex items-baseline text-base font-variant-all-small-caps space-x-1">
                                    <div v-if="site.lease_or_own == 'Owned'">Owned</div>
                                    <div v-else-if="site.leases.length && site.due_in_days > 0">Due in <span class="font-bold">{{ site.due_in_text }}</span></div>
                                    <div v-else-if="site.leases.length && site.due_in_days == 0">Expired</div>
                                    <div v-else>No Lease Information</div>
                                </div>
                            </div>
                        </td>
                        <td data-precision="0" :data-originalvalue="site.lease_cost" class="is-currency text-right">{{ formatCurrency(site.lease_cost, 0) }}</td>
                        <td class="text-right">
                            <div class="badge badge-red">
                                <div class="flex items-baseline space-x-1">
                                    <span :data-originalvalue="site.shortened_remaining_lease_cost" class="is-shorthand-currency font-extrabold">${{ site.shortened_remaining_lease_cost }}</span>
                                </div>
                            </div>
                        </td>
                        <td class="text-right font-semibold">
                            <div v-if="site.utilization" class="badge" :class="site.utilization_color">
                                <div class="flex items-baseline space-x-1">
                                    <span class="font-extrabold">{{ site.utilization }}%</span>
                                    <span class="text-base font-variant-all-small-caps">Utilized</span>
                                </div>
                            </div>
                            <div v-else>-</div>
                        </td>
                        <td class="text-right">
                            {{ site.npv_discount_rate ? site.npv_discount_rate + "%" : "-" }}
                        </td>
                        <td class="text-right">
                            <div :data-originalvalue="site.shortened_npv" class="is-shorthand-currency badge badge-green font-extrabold">
                                ${{ site.shortened_npv }}
                            </div>
                        </td>
                        <td class="text-right">
                            <div :data-originalvalue="site.shortened_estimated_waste" class="is-shorthand-currency badge badge-red font-extrabold">
                                ${{ site.shortened_estimated_waste }}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <portal-target name="no-sites-found"></portal-target>
    </div>
</template>

<script>   
    import { mapState } from 'vuex';

    export default {
        props: {
            siteData: {
              type: Array,
              required: true
            },

            showHeader: {
              type: Boolean,
              default: true
            },

            smallHeader: {
              type: Boolean,
              default: false
            },

            showFilters: {
              type: Boolean,
              default: true
            },

            centerCards: {
                type: Boolean,
                default: false
            },

            view: {
                type: String,
                default: 'grid'
            }
        },

        data() {
            return {
                layout: this.view,
                showArchivedSites: false,
            }
        },

        mounted() {
            if (localStorage.getItem('reopt_sites_layout')) {
                this.layout = localStorage.getItem('reopt_sites_layout');
            }

            this.sites.all = this.siteData
            this.sites.filtered = this.siteData.filter(site => {
                return site.archived_at == null;
            });

            this.$emit('filterRequest');
        },

        computed: {
            ...mapState(['sites']),

            filters() {
                return {
                    showArchivedSites: this.showArchivedSites
                }
            },

            // npv is in cents and estimated_waste in dollars, but this component renders
            // through the global formatCurrency mixin, which divides by 100. Both values
            // are therefore kept in cents here so that mixin lands on dollars.
            totalNPV() {
                return _.sumBy(this.sites.filtered, 'npv');
            },

            totalWaste() {
                return _.sumBy(this.sites.filtered, 'estimated_waste');
            },

            wastePercentage() {
                if (this.totalNPV === 0) return 0;

                return Math.ceil((this.totalWaste / (this.totalNPV/100)) * 100);
            }
        }
    }
</script>
